import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { History, Search, Download, X as XIcon } from 'lucide-react';
import { toast } from 'sonner';

const ACCION_STYLE = {
  crear: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  editar: 'border-amber-200 bg-amber-50 text-amber-700',
  eliminar: 'border-red-200 bg-red-50 text-red-700',
  auto_cierre_aceptado: 'border-blue-200 bg-blue-50 text-blue-700',
  auto_cierre_rechazado: 'border-purple-200 bg-purple-50 text-purple-700',
};

const ACCION_LABEL = {
  crear: 'Crear',
  editar: 'Editar',
  eliminar: 'Eliminar',
  auto_cierre_aceptado: 'Cierre auto aceptado',
  auto_cierre_rechazado: 'Cierre auto rechazado',
};

const ESTADO_BADGE = {
  aceptado: { text: 'Aceptado por empleado', className: 'bg-emerald-100 text-emerald-800' },
  rechazado: { text: 'Rechazado por empleado', className: 'bg-red-100 text-red-800' },
  pendiente: { text: 'Pendiente validación', className: 'bg-amber-100 text-amber-800' },
};

const csvEsc = (v) => {
  const s = v == null ? '' : String(v);
  return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export default function AuditoriaSection() {
  const [items, setItems] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [filtroAccion, setFiltroAccion] = useState('todas');
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');

  useEffect(() => {
    api.get('/admin/auditoria').then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const filtrados = items.filter((a) => {
    if (filtroAccion !== 'todas' && a.accion !== filtroAccion) return false;
    if (filtroEstado !== 'todos') {
      const est = a.estado || 'pendiente';
      if (est !== filtroEstado) return false;
    }
    if (busqueda) {
      const q = busqueda.toLowerCase();
      const hay = `${a.detalle || ''} ${a.por || ''} ${a.comentario_empleado || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (desde || hasta) {
      const fecha = (a.timestamp || '').slice(0, 10);  // YYYY-MM-DD
      if (desde && fecha < desde) return false;
      if (hasta && fecha > hasta) return false;
    }
    return true;
  });

  const limpiarFiltros = () => {
    setBusqueda(''); setFiltroAccion('todas'); setFiltroEstado('todos'); setDesde(''); setHasta('');
  };
  const hayFiltros = busqueda || filtroAccion !== 'todas' || filtroEstado !== 'todos' || desde || hasta;

  const exportarCSV = () => {
    if (filtrados.length === 0) { toast.error('No hay registros que exportar con los filtros actuales'); return; }
    const cabecera = ['Fecha', 'Hora', 'Acción', 'Detalle', 'Por', 'Estado', 'Comentario empleado'];
    const filas = filtrados.map((a) => {
      const d = new Date(a.timestamp);
      const fecha = isNaN(d) ? '' : d.toLocaleDateString('es-ES', { timeZone: 'Europe/Madrid' });
      const hora = isNaN(d) ? '' : d.toLocaleTimeString('es-ES', { timeZone: 'Europe/Madrid' });
      return [fecha, hora, a.accion || '', a.detalle || '', a.por || '', a.estado || 'pendiente', a.comentario_empleado || ''];
    });
    // BOM UTF-8 para que Excel reconozca los acentos + separador ;
    const csv = '\ufeff' + [cabecera, ...filas].map((r) => r.map(csvEsc).join(';')).join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const hoy = new Date().toISOString().slice(0, 10);
    a.href = url; a.download = `auditoria_fichajes_${hoy}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exportadas ${filtrados.length} filas a CSV`);
  };

  return (
    <Card className="border-zinc-200" data-testid="auditoria-section">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="font-heading flex items-center gap-2 text-lg">
              <History className="h-5 w-5" /> Registro de auditoría
              <Badge variant="secondary" className="ml-1 font-normal">{filtrados.length} de {items.length}</Badge>
            </CardTitle>
            <CardDescription className="mt-1">
              Historial de cambios manuales en fichajes: quién, cuándo y si el empleado los aceptó (obligatorio por normativa 2026).
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={exportarCSV} data-testid="auditoria-exportar-csv" className="shrink-0">
            <Download className="h-4 w-4 mr-1.5" /> Exportar CSV
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs">Buscar</Label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-400" />
              <Input value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Empleado, motivo, comentario…" className="pl-8"
                data-testid="auditoria-buscar" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Desde</Label>
            <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)}
              max={hasta || undefined} data-testid="auditoria-fecha-desde" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Hasta</Label>
            <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)}
              min={desde || undefined} data-testid="auditoria-fecha-hasta" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Acción</Label>
            <Select value={filtroAccion} onValueChange={setFiltroAccion}>
              <SelectTrigger data-testid="auditoria-filtro-accion"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas</SelectItem>
                <SelectItem value="crear">Crear</SelectItem>
                <SelectItem value="editar">Editar</SelectItem>
                <SelectItem value="eliminar">Eliminar</SelectItem>
                <SelectItem value="auto_cierre_aceptado">Cierre auto aceptado</SelectItem>
                <SelectItem value="auto_cierre_rechazado">Cierre auto rechazado</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 sm:col-span-2 flex flex-col justify-end">
            <div className="flex flex-wrap items-center gap-2">
              {['todos', 'pendiente', 'aceptado', 'rechazado'].map((k) => (
                <button key={k} onClick={() => setFiltroEstado(k)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium transition ${filtroEstado === k ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'}`}
                  data-testid={`auditoria-filtro-estado-${k}`}>
                  {k === 'todos' ? 'Todos' : k.charAt(0).toUpperCase() + k.slice(1)}
                </button>
              ))}
              {hayFiltros && (
                <button onClick={limpiarFiltros}
                  className="px-2.5 py-1 rounded-full text-xs font-medium text-zinc-500 hover:text-zinc-800 border border-zinc-200 hover:bg-zinc-50 inline-flex items-center gap-1"
                  data-testid="auditoria-limpiar-filtros">
                  <XIcon className="h-3 w-3" /> Limpiar filtros
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="space-y-2 max-h-[32rem] overflow-y-auto">
          {filtrados.length === 0 && (
            <p className="text-zinc-400 text-sm text-center py-6">
              {items.length === 0 ? 'Sin cambios registrados.' : 'Sin resultados con los filtros actuales.'}
            </p>
          )}
          {filtrados.map((a, i) => {
            const est = ESTADO_BADGE[a.estado] || ESTADO_BADGE.pendiente;
            return (
              <div key={i} className="flex items-start gap-3 text-sm border-b border-zinc-100 pb-2.5">
                <Badge variant="outline" className={`capitalize shrink-0 ${ACCION_STYLE[a.accion] || ''}`}>{ACCION_LABEL[a.accion] || a.accion}</Badge>
                <div className="flex-1 min-w-0">
                  <p className="text-zinc-700 break-words">{a.detalle}</p>
                  <p className="text-xs text-zinc-400 mt-0.5">
                    {a.por} · {new Date(a.timestamp).toLocaleString('es-ES', { timeZone: 'Europe/Madrid' })}
                  </p>
                  {a.comentario_empleado && (
                    <p className="text-xs text-red-500 mt-0.5">Comentario empleado: {a.comentario_empleado}</p>
                  )}
                </div>
                <Badge className={`${est.className} shrink-0`}>{est.text}</Badge>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
